/**
 * Convention « plan » des positions personnalisées du catalogue.
 *
 * Une position >= 100 encode un numéro de plan à un chiffre par niveau :
 * 100 ↔ 1, 110 ↔ 1.1, 112 ↔ 1.1.2, 200 ↔ 2 … (centaines = 1er niveau,
 * dizaines = 2e, unités = 3e). Le tri numérique suit l'ordre du plan et les
 * zéros finaux donnent le niveau de retrait affiché côté manager.
 *
 * En dessous de 100 : position par défaut du backend (0..n), le niveau de
 * retrait reste calculé par les règles automatiques (kind read/write).
 *
 * Limite assumée : 9 éléments au plus par niveau sous un même parent.
 */

export const OUTLINE_BASE = 100

export const isOutlinePosition = (position?: number): boolean =>
    position != null && position >= OUTLINE_BASE

// niveau de retrait (0, 1 ou 2) d'une position encodée
export const outlineIndent = (position: number): number => {
    if (position % 100 === 0) {
        return 0
    }
    if (position % 10 === 0) {
        return 1
    }
    return 2
}

// « 112 » → « 1.1.2 », pour afficher le numéro de plan tel qu'il est stocké
export const formatOutline = (position: number): string => {
    const digits = [Math.floor(position / 100), Math.floor(position / 10) % 10, position % 10]
    while (digits.length > 1 && digits[digits.length - 1] === 0) {
        digits.pop()
    }
    return digits.join('.')
}

// encode une liste (dans l'ordre d'affichage) de niveaux de retrait en
// numéros de plan ; les niveaux incohérents sont ramenés au plus proche
// valide (pas plus profond que le précédent + 1), les compteurs plafonnent à 9.
// `context` = niveaux de lignes virtuelles précédant la liste (ex. le titre
// de la carte et le toggle write côté manager, qui portent les niveaux 0 et 1
// sans être des actions) : elles amorcent la numérotation sans être rendues
export const encodeOutline = (indents: number[], context: number[] = []): number[] => {
    const counters = [0, 0, 0]
    let previous = -1
    const push = (raw: number) => {
        const indent = Math.max(0, Math.min(raw, previous + 1, 2))
        previous = indent
        counters[indent] = Math.min(counters[indent] + 1, 9)
        for (let level = indent + 1; level < counters.length; level++) {
            counters[level] = 0
        }
        return counters[0] * 100 + counters[1] * 10 + counters[2]
    }
    context.forEach(push)
    return indents.map(push)
}
